import { useCallback, useEffect, useMemo, useState } from "react";

import {
  isMultiApprovalRequest,
  canUserDeclineRequest,
  MultiApprovalWorkflow,
} from "../../MultiApprovals/Api/multiapprovalapi";

type RequestWithWorkflow = {
  id: number | string;
  requestStatus?: string;
  status?: string;
  multiApprovalWorkflow?: MultiApprovalWorkflow | null;
};

function requestKey(id: number | string | null | undefined): string | null {
  if (id == null) {
    return null;
  }
  return String(id);
}

function resolveWorkflow(request: RequestWithWorkflow | undefined): MultiApprovalWorkflow | null {
  return isMultiApprovalRequest(request?.multiApprovalWorkflow) ? request.multiApprovalWorkflow : null;
}

export function useMultiApprovalWorkflow(requests: RequestWithWorkflow[]) {
  const [detailsRequestId, setDetailsRequestId] = useState<string | null>(null);
  const [detailsWorkflow, setDetailsWorkflow] = useState<MultiApprovalWorkflow | null>(null);

  const workflowsByRequestId = useMemo(() => {
    const mapped: Record<string, MultiApprovalWorkflow> = {};
    requests.forEach((request) => {
      const key = requestKey(request.id);
      if (key && isMultiApprovalRequest(request.multiApprovalWorkflow)) {
        mapped[key] = request.multiApprovalWorkflow;
      }
    });
    return mapped;
  }, [requests]);

  useEffect(() => {
    if (detailsRequestId == null) {
      return;
    }
    const request = requests.find((item) => requestKey(item.id) === detailsRequestId);
    setDetailsWorkflow(resolveWorkflow(request));
  }, [requests, detailsRequestId]);

  const openDetailsWorkflow = useCallback((request: RequestWithWorkflow) => {
    setDetailsRequestId(requestKey(request.id));
    setDetailsWorkflow(resolveWorkflow(request));
  }, []);

  const closeDetailsWorkflow = useCallback(() => {
    setDetailsRequestId(null);
    setDetailsWorkflow(null);
  }, []);

  const canUserApprove = (requestId: number | string) => {
    const workflow = workflowsByRequestId[requestKey(requestId) ?? ""];
    if (!isMultiApprovalRequest(workflow)) {
      return true;
    }
    return workflow.currentUserCanApprove;
  };

  const canUserDecline = (requestId: number | string) => {
    const workflow = workflowsByRequestId[requestKey(requestId) ?? ""];
    return canUserDeclineRequest(workflow);
  };

  const canUserDeclineDetails = () => canUserDeclineRequest(detailsWorkflow);

  const getWorkflowForRequest = (requestId: number | string) =>
    workflowsByRequestId[requestKey(requestId) ?? ""];

  return {
    workflowsByRequestId,
    detailsWorkflow,
    openDetailsWorkflow,
    closeDetailsWorkflow,
    canUserApprove,
    canUserDecline,
    canUserDeclineDetails,
    getWorkflowForRequest,
  };
}
